import "react-international-phone/style.css";

import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Center,
  Checkbox,
  Group,
  Input,
  NumberInput,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput, DatePickerInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  ActionFunctionArgs,
  createCookie,
  HeadersArgs,
  LoaderFunction,
  MetaFunction,
} from "@remix-run/node";
import {
  Form,
  json,
  Link,
  redirect,
  useFetcher,
  useLoaderData,
} from "@remix-run/react";
import {
  IconArrowLeft,
  IconAt,
  IconCalendar,
  IconDiscountCheckFilled,
  IconExclamationCircle,
  IconFileTypeJpg,
  IconFileTypePdf,
  IconFileUpload,
  IconInfoCircle,
  IconRosetteDiscount,
  IconTrash,
} from "@tabler/icons-react";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { PhoneInput } from "react-international-phone";
import { RefinementCtx, z } from "zod";

import MasterClassImage from "~/assets/misc/masterclass.webp";
import { PhoneNumberVerificationModal } from "~/components/phone-number-verification-modal";
import { Trans } from "~/components/typed-trans";
import { DOCUMENT_FILE_TYPES, IMAGE_FILE_TYPES, LARUCHE_IDENTIFIER, MAX_FILENAME_LENGTH_BEFORE_UPLOAD,MEGABYTE, PAGE_TITLE } from "~/constants";
import { getGenderValues } from "~/data/dropdown-values";
import { useCountdown } from "~/hooks/use-countdown";
import { useProlongedNavigationState } from "~/hooks/use-prolonged-navigation-state";
import { useTranslation } from "~/hooks/use-typed-translation";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import sharedClasses from "~/routes/($lang)._index/book-doctor.module.scss";
import { loader as serviceFormLoaderFunc } from "~/routes/api.service-form/route";
import { getMasterclassesBySlug } from "~/services/get-masterclass-by-slug";
import utilityClasses from "~/styles/utilities.module.scss";
import { OkDataError } from "~/types";
import { FormField } from "~/types/form-field";
import { Masterclass } from "~/types/masterclass";
import { api } from "~/utils/api.server";
import { cleanPhoneNumber } from "~/utils/clean-phone-number";
import { resolveCurrencyContext } from "~/utils/currency.server";
import { getCentralizedMeta } from "~/utils/meta-helper";
import { truncateWithEllipsis } from "~/utils/truncate-text";

import classes from "./book-masterclass.module.scss";

// i18next.t() no longer works for some reason, so I'm passing t() from the render function
const baseSchema = (t: any) =>
  z.object({
    firstName: z.string().min(1, t("first-name-required")),
    surname: z.string().min(1, t("surname-required")),
    dob: z
      .date()
      .nullable() // Start as null
      .refine(val => val !== null, {
        // Don't allow form to be submitted if dob is null
        message: t("dob-required"),
      }),
    gender: z
      .string()
      .nullable() // Start as null
      .refine(val => val !== null, {
        // Don't allow form to be submitted if gender is null
        message: t("gender-required"),
      }),
    phoneNumber: z.string(),
    whatsappPhoneNumber: z.string(),
    email: z.string().email().optional().or(z.literal("")),
  });

const superRefineLogic = (t: any, data: any, ctx: RefinementCtx) => {
  // Only validate the field that should be filled
  const phoneNumberHasContent =
    data.phoneNumber && data.phoneNumber.trim().length > 4;
  const isPhoneNumberDisplayed = data.phoneNumber.trim().length > 0;
  const whatsappHasContent =
    data.whatsappPhoneNumber && data.whatsappPhoneNumber.trim().length > 4;
  const isWhatsappNumberDisplayed = data.whatsappPhoneNumber.trim().length > 0;

  // console.log("phoneNumberHasContent:", phoneNumberHasContent);
  // console.log("whatsappHasContent:", whatsappHasContent);

  if (!phoneNumberHasContent && isPhoneNumberDisplayed) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("at-least-one-phone-number-required"),
      path: ["phoneNumber"],
    });
    return;
  }
  if (!whatsappHasContent && isWhatsappNumberDisplayed) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("at-least-one-phone-number-required"),
      path: ["whatsappPhoneNumber"],
    });
    return;
  }

  // If phone number has content but is too short, validate it
  if (phoneNumberHasContent && data?.phoneNumber?.trim()?.length < 11) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("phone-number-too-short"),
      path: ["phoneNumber"],
    });
  }

  // If whatsapp has content but is too short, validate it
  if (whatsappHasContent && data?.whatsappPhoneNumber?.trim()?.length < 11) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("phone-number-too-short"),
      path: ["whatsappPhoneNumber"],
    });
  }
};

const schema = (t: any) =>
  baseSchema(t).superRefine((data, ctx) => superRefineLogic(t, data, ctx));

type Inputs = z.infer<ReturnType<typeof schema>>;

// File upload component for dynamic form fields
const FileUploadField = ({
  field,
  t,
}: {
  field: FormField;
  t: any;
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const maxFiles = field.fileOptions?.maxFiles ?? 10;
      const maxSize = (field.fileOptions?.maxSize ?? 10) * MEGABYTE;

      // Filter files by size
      const validFiles = acceptedFiles.filter(file => {
        if (file.size > maxSize) {
          return false;
        }
        return true;
      });

      // Limit total files
      const newFiles = [...uploadedFiles, ...validFiles].slice(0, maxFiles);
      setUploadedFiles(newFiles);
    },
    [uploadedFiles, field.fileOptions]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: field.fileOptions?.accept
      ? {
          "image/*": IMAGE_FILE_TYPES,
          "application/pdf": DOCUMENT_FILE_TYPES,
        }
      : undefined,
    onDrop,
    multiple: field.fileOptions?.multiple ?? true,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  return (
    <Stack key={field.id} gap="0.5rem" data-additional-form data-field-id={field.id}>
      <Input.Label required={field.required}>{field.label}</Input.Label>
      <div className={classes["files-wrapper"]}>
        <div
          {...getRootProps({
            className: `${classes.dropzone} ${
              isDragActive ? classes.active : ""
            } ${isDragAccept ? classes.accept : ""} ${
              isDragReject ? classes.reject : ""
            }`,
          })}>
          <input
            {...getInputProps()}
            name={field.id}
            data-file-upload
          />

          <Stack align="center">
            <div className={classes["icon-wrapper"]}>
              <IconFileUpload color="white" />
            </div>
            <Stack gap="0.5rem" ta="center">
              <Text fz={14} fw={500}>
                {t("drag-file-here")}
              </Text>
              <Text fz={13} c="dimmed">
                {t("drag-file-here-continuation")}
              </Text>
            </Stack>
          </Stack>
        </div>
        {uploadedFiles.length > 0 && (
          <div className={classes["uploaded-files"]}>
            <Title order={4} fw={500} fz={15}>
              {t("uploaded-files")}
            </Title>
            <ul>
              {uploadedFiles.map(file => (
                <li key={file.name}>
                  <div className={classes.content}>
                    {file.type === "application/pdf" ? (
                      <IconFileTypePdf />
                    ) : (
                      <IconFileTypeJpg />
                    )}
                    <p>
                      {truncateWithEllipsis(
                        file.name,
                        MAX_FILENAME_LENGTH_BEFORE_UPLOAD
                      )}{" "}
                      ({(file.size / MEGABYTE).toFixed(2)} {t("mb")})
                    </p>
                  </div>

                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => removeFile(file)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Stack>
  );
};

const renderField = (field: FormField, t: any) => {
  switch (field.inputType) {
    case "text":
      return (
        <TextInput
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          name={field.id}
          data-additional-form
        />
      );
    case "email":
      return (
        <TextInput
          key={field.id}
          label={field.label}
          type="email"
          leftSection={<IconAt size={16} stroke={1.5} />}
          required={field.required}
          placeholder={field.placeholder}
          name={field.id}
          data-additional-form
        />
      );
    case "radio":
      return (
        <Radio.Group
          key={field.id}
          label={field.label}
          required={field.required}
          name={field.id}
          data-additional-form>
          {field.options?.map(option => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
              mt="0.25rem"
            />
          ))}
        </Radio.Group>
      );
    case "number":
      return (
        <NumberInput
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          min={0}
          name={field.id}
          data-additional-form
        />
      );
    case "checkbox":
      return (
        <Checkbox.Group
          key={field.id}
          label={field.label}
          required={field.required}>
          {field.options?.map(option => (
            <Checkbox
              key={option.value}
              value={option.value}
              label={option.label}
              mt="0.25rem"
              name={field.id}
              data-additional-form
            />
          ))}
        </Checkbox.Group>
      );

    case "textarea":
      return (
        <Textarea
          key={field.id}
          label={field.label}
          rows={5}
          resize="vertical"
          placeholder={field.placeholder}
          required={field.required}
          name={field.id}
          data-additional-form
        />
      );

    case "date":
      return (
        <DatePickerInput
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          data-additional-form
          data-name={field.id}
        />
      );

    case "select":
      return (
        <Select
          key={field.id}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          data={field.options}
          data-additional-form
          data-name={field.id}
        />
      );

    case "file":
      return <FileUploadField key={field.id} field={field} t={t} />;

    default:
      return null;
  }
};

const getAdditionalFormData = (ref: HTMLElement): Record<string, string> => {
  const elements = ref.querySelectorAll("[data-additional-form]");
  const formData: Record<string, string> = {};

  elements.forEach(element => {
    const input = element as HTMLInputElement;
    const name = input.name || element.getAttribute("data-name");
    const fieldId = element.getAttribute("data-field-id");

    if (element.hasAttribute("data-file-upload") || fieldId) {
      return;
    }

    if (name) {
      if (input.type === "checkbox") {
        if (input.checked) {
          if (!formData[name]) formData[name] = "";
          formData[name] = formData[name]
            ? `${formData[name]},${input.value}`
            : input.value;
        }
      } else if (input.type === "button") {
        formData[name] = input.textContent ?? "";
      } else {
        formData[name] = input.value;
      }
    }
  });

  return formData;
};

const getUploadedFiles = (ref: HTMLElement): File[] => {
  const fileInputs = ref.querySelectorAll<HTMLInputElement>("[data-file-upload]");
  const files: File[] = [];

  fileInputs.forEach(input => {
    if (input.files) {
      Array.from(input.files).forEach(file => files.push(file));
    }
  });

  return files;
};

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];

  const masterclassData = data.data as Masterclass;
  const doctorImage = masterclassData?.providerDetails?.image;

  return getCentralizedMeta({
    title: data.metaTitle,
    description: data.metaDescription,
    image: doctorImage,
    url: data.metaOrigin + location.pathname,
    locale: data.metaLocale,
    keywords: `Ivory Coast, Côte d'Ivoire, Masterclass`,
    alternateLanguages: {
      en: `${data.metaOrigin}/en/masterclass/${data.masterClassSlug}`,
      fr: `${data.metaOrigin}/fr/masterclass/${data.masterClassSlug}`,
    }
  });
};

export const headers = ({ actionHeaders, loaderHeaders }: HeadersArgs) => {
  if (loaderHeaders) {
    return loaderHeaders;
  }
  // Otherwise return action headers
  return actionHeaders;
};

export default function Route() {

  const { t, i18n } = useTranslation();

  const emailRef = useRef<HTMLInputElement>(null);
  const {
    isNavigating: isSubmitting,
    setIsNavigating: setIsSubmitting,
    reset: resetIsSubmitting,
  } = useProlongedNavigationState();

  const [
    phoneNumberVerificationModalOpened,
    {
      open: openPhoneNumberVerificationModal,
      close: closePhoneNumberVerificationModal,
    },
  ] = useDisclosure(false);
  const [isAllowedToBook, setIsAllowedToBook] = useState(false);
  const { timeLeft, isComplete, reset: resetTimer } = useCountdown(90, true);
  const [noWhatsappNumber, setNoWhatsappNumber] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [isPromoCodeValid, setIsPromoCodeValid] = useState("");
  const [formFields, setFormFields] = useState<FormField[]>([]);

  const actionFetcher = useFetcher<{ error?: string }>();
  const serviceFormFetcher = useFetcher<typeof serviceFormLoaderFunc>();
  const promoCodeFetcher = useFetcher<
    OkDataError<{
      isValid: boolean;
      discountPercent: number;
      services: string[];
    }>
  >();

  const loaderData = useLoaderData<typeof loader>();
  const masterClassData = loaderData?.data as Masterclass;

  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm({
    // Controlled mode allows Select to properly work when changing language and also allows service time disabling logic to work properly when service date is changed
    mode: "controlled",
    validate: zodResolver(schema(t)),
    initialValues: {
      firstName: "",
      surname: "",
      dob: null as Date | null,
      gender: null as string | null,
      phoneNumber: "",
      whatsappPhoneNumber: "",
      email: "",
      language: i18n.language,
    },
  });

  const handleFormSubmit = (values: Inputs) => {
    import.meta.env.DEV && console.log("[DEV] ", values);
    if (import.meta.env.DEV) {
      setIsAllowedToBook(true);
    } else {
      openPhoneNumberVerificationModal();
    }
  };

  useEffect(() => {
    if (masterClassData?.id) {
      serviceFormFetcher.load(`/api/service-form?serviceId=${masterClassData.id}`);
    }
  }, [masterClassData?.id]);

  useEffect(() => {
    if (serviceFormFetcher.data) {
      setFormFields(serviceFormFetcher.data?.data ?? []);
    }
  }, [serviceFormFetcher.data]);

  useEffect(() => {
    if (isAllowedToBook) {
      const formData = new FormData();
      Object.entries(form.getValues()).forEach(([key, value]) => {
        formData.append(key, value?.toString() ?? "");
      });

      formData.append("masterclassId", masterClassData.id);
      formData.append("dob", form.getValues().dob?.toISOString() ?? "");
      formData.append("cost", masterClassData.originalPrice?.toString() || masterClassData.price.toString());
      formData.append("masterClassImage", masterClassData.banner || "");
      formData.append("promoCode", promoCode);
      formData.append("discountPercent", discountPercent?.toString() || "0");

      const additionalFormData = getAdditionalFormData(
        formRef?.current as HTMLFormElement
      );
      formData.append("formData", JSON.stringify(additionalFormData));

      const uploadedFiles = getUploadedFiles(formRef?.current as HTMLFormElement);
      uploadedFiles.forEach(file => {
        formData.append("files", file);
      });

      actionFetcher.submit(formData, {
        method: "post",
        encType: "multipart/form-data",
      });
      setIsAllowedToBook(false);
    }
  }, [isAllowedToBook]);

  useEffect(() => {
    if (actionFetcher.state === "submitting" || serviceFormFetcher.state === "submitting") {
      setIsSubmitting(true);
    }
  }, [actionFetcher.state, serviceFormFetcher.state]);

  useEffect(() => {
    if (noWhatsappNumber) {
      form.setFieldValue("whatsappPhoneNumber", "");
    } else {
      form.setFieldValue("phoneNumber", "");
    }
  }, [noWhatsappNumber]);

  useEffect(() => {
    // console.log(actionFetcher.data?.error);
    if (actionFetcher.data?.error) {
      resetIsSubmitting();
    }
  }, [actionFetcher.data]);

  useEffect(() => {
    if (promoCodeFetcher.data?.data) {
      const services = promoCodeFetcher.data.data?.services;
      const isValid = promoCodeFetcher.data.data?.isValid;
      // console.log(isValid, services);
      // if (services?.includes(serviceType.value)) {
      //   // console.log(promoCodeFetcher.data.data);
      //   setIsPromoCodeValid(promoCodeFetcher.data?.data.isValid.toString());
      //   setDiscountPercent(promoCodeFetcher.data?.data.discountPercent);
      // }
      // else
      if (!services?.length && isValid) {
        // Repeating this block of code intentionally - Apply promocode to all services
        setIsPromoCodeValid(promoCodeFetcher.data?.data.isValid.toString());
        setDiscountPercent(promoCodeFetcher.data?.data.discountPercent);
      } else {
        setIsPromoCodeValid("false");
      }
    }
  }, [promoCodeFetcher.data]);

  return (
    <div className={classes.section}>
      <div className={utilityClasses["small-container"]}>
        <Form
          ref={formRef}
          method="post"
          className={sharedClasses.body}
          onSubmit={form.onSubmit(
            values => handleFormSubmit(values as Inputs),
            errors => {
              import.meta.env.DEV && console.log("[DEV] Form errors:", errors);
              const firstErrorPath = Object.keys(errors)[0];
              form.getInputNode(firstErrorPath)?.focus();
            }
          )}>
          <Stack justify="start">
            <Button
              w="fit-content"
              component={Link}
              to={`/${i18n.language}/health-library`}
              reloadDocument
              type="button"
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={16} />}>
              {t("go-back")}
            </Button>

            <div className={classes["img-wrapper"]}>
              <img src={masterClassData?.banner ?? MasterClassImage} alt="" />
            </div>
          </Stack>
          <Title
            order={2}
            fw={600}
            fz={20}
            mt={"2rem"}
            ta={"center"}
            className={sharedClasses["body-heading"]}>
            {t("fill-details-to-enroll")}
          </Title>

          <Group
            align="end"
            w={{ base: "100%", sm: "calc(50% - 1rem)" }}
            mt={"1rem"}
            gap={5}>
            <TextInput
              disabled={isPromoCodeValid === "true"}
              label={t("promo-code")}
              flex={1}
              placeholder={t("eg", { example: "ABCD123" })}
              rightSection={
                <IconRosetteDiscount size={20} stroke={1.5} color="green" />
              }
              value={promoCode}
              onChange={e => setPromoCode(e.target.value as string)}
            />
            <Button
              disabled={!promoCode || isPromoCodeValid === "true"}
              loading={promoCodeFetcher.state === "submitting"}
              size="xs"
              variant="outline"
              type="button"
              h={36}
              onClick={() => {
                if (!promoCode) return;
                promoCodeFetcher.submit(null, {
                  method: "post",
                  action: `/api/validate-promo-code/${promoCode}`,
                });
              }}>
              {t("apply")}
            </Button>
          </Group>

          {isPromoCodeValid === "true" ? (
            <Alert
              mt="1rem"
              variant="light"
              color={"green"}
              radius={"sm"}
              icon={<IconDiscountCheckFilled />}
              // withCloseButton
              onClose={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"green"}>
                <Trans
                  i18nKey="promo-code-discount-x"
                  values={{ discountPercent }}
                  components={{ bold: <Text span inherit fw={600} /> }}
                />
              </Text>
            </Alert>
          ) : null}

          {isPromoCodeValid === "false" ? (
            <Alert
              withCloseButton
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              onClick={() => setIsPromoCodeValid("")}>
              <Text fz={14} lh={1.5} c={"red"}>
                {t("promo-code-invalid")}
              </Text>
            </Alert>
          ) : null}

          <Group
            justify="space-between"
            align="start"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("first-name")}
              placeholder={t("eg", { example: "John" })}
              {...form.getInputProps("firstName")}
              key={form.key("firstName")}
              withAsterisk
            />
            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("surname")}
              placeholder={t("eg", { example: "Doe" })}
              {...form.getInputProps("surname")}
              key={form.key("surname")}
              withAsterisk
            />
          </Group>
          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            {/* <DatePickerInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder={t("select-dob")}
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            /> */}
            <DateInput
              w={{ base: "100%", sm: "48%" }}
              label={t("dob")}
              placeholder="DD/MM/YYYY"
              leftSection={<IconCalendar size={16} stroke={1.5} />}
              {...form.getInputProps("dob")}
              key={form.key("dob")}
              withAsterisk
              maxDate={new Date()}
            />
            <Select
              w={{ base: "100%", sm: "48%" }}
              label={t("gender")}
              placeholder={t("select-gender")}
              data={getGenderValues(t)}
              {...form.getInputProps("gender")}
              key={form.key("gender")}
              withAsterisk
            />
          </Group>
          <Group
            align="start"
            justify="space-between"
            mt={"1rem"}
            flex={{ base: "unset", sm: 1 }}>
            <Stack gap="0.25rem" w={{ base: "100%", sm: "48%" }}>
              {noWhatsappNumber ? (
                <Input.Wrapper
                  label={t("phone-number")}
                  w={{ base: "100%" }}
                  withAsterisk>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.phoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.phoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("phoneNumber")}
                    key={form.key("phoneNumber")}
                  />
                  <Input.Error mt={5}>{form.errors.phoneNumber}</Input.Error>
                </Input.Wrapper>
              ) : (
                <Input.Wrapper
                  label={t("whatsapp-phone-number")}
                  description={t("whatsapp-number-reason")}
                  withAsterisk
                  w={{ base: "100%" }}>
                  <PhoneInput
                    className={classes["react-international-phone"]}
                    countrySelectorStyleProps={{
                      className: `${
                        form.errors.whatsappPhoneNumber ? classes.error : ""
                      } ${
                        classes["react-international-phone-country-selector"]
                      }`,
                    }}
                    inputClassName={`${
                      form.errors.whatsappPhoneNumber ? classes.error : ""
                    } ${classes["react-international-phone-input"]}`}
                    defaultCountry="ci"
                    {...form.getInputProps("whatsappPhoneNumber")}
                    key={form.key("whatsappPhoneNumber")}
                  />
                  <Input.Error mt={5}>
                    {form.errors.whatsappPhoneNumber}
                  </Input.Error>
                </Input.Wrapper>
              )}

              <Checkbox
                label={t("no-whatsapp-number")}
                size="xs"
                checked={noWhatsappNumber}
                onChange={e => setNoWhatsappNumber(e.currentTarget.checked)}
              />
            </Stack>

            <TextInput
              w={{ base: "100%", sm: "48%" }}
              label={t("email")}
              type="email"
              leftSection={<IconAt size={16} stroke={1.5} />}
              placeholder={t("eg", { example: "johndoe@example.com" })}
              {...form.getInputProps("email")}
              key={form.key("email")}
              ref={emailRef}
            />
          </Group>

          {/* Additional form fields start here --------------------------- */}
          {formFields.length ? (
            <Title order={2} fw={500} fz={18} mt={"3rem"}>
              {t("extra-info")}
            </Title>
          ) : null}

          <Stack gap={"xs"} mt={"1rem"}>
            {formFields.map(field => renderField(field, t))}
          </Stack>
          {/* Additional form fields end here ---------------------------- */}

          {actionFetcher.data?.error && (
            <Alert
              mt="1rem"
              variant="light"
              color={"red"}
              radius={"sm"}
              icon={<IconExclamationCircle />}
              title={
                <Text c={"red"} fw={500} fz={14} lh={1.5}>
                  {t("oops")}
                </Text>
              }>
              <Text fz={14} lh={1.5} c={"red"}>
                {t("enroll-error")}
              </Text>
            </Alert>
          )}
          <PhoneNumberVerificationModal
            whatsappPhoneNumber={
              form.values.whatsappPhoneNumber.trim().length > 3
                ? form.values.whatsappPhoneNumber
                : ""
            }
            phoneNumber={
              form.values.phoneNumber.trim().length > 3
                ? form.values.phoneNumber
                : ""
            }
            opened={phoneNumberVerificationModalOpened}
            close={closePhoneNumberVerificationModal}
            setIsAllowedToBook={setIsAllowedToBook}
            timeLeft={timeLeft}
            isComplete={isComplete}
            resetTimer={resetTimer}
          />
          <Button
            type="submit"
            radius={"xl"}
            w={"100%"}
            mt={"3rem"}
            loading={isSubmitting}>
            {t("enroll")}
          </Button>
          <Text fz={12} c="dimmed" fs={"italic"} mt={"0.5rem"} ta="center">
            <Trans i18nKey="agree-to-privacy-policy-by-enrolling">
              By enrolling, you agree to our{" "}
              <Anchor
                component={Link}
                target="_blank"
                to="https://docs.google.com/document/d/1KxHsr6HBjC6KN3ms_LdKcoCZYdIQOSN5/edit"
                inherit>
                privacy policy
              </Anchor>
              .
            </Trans>
          </Text>
        </Form>
      </div>
    </div>
  );
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;
  const masterClassSlug = params?.["class-slug"] ?? "";

  const currencyContext = (await resolveCurrencyContext(request, true)) ?? {
    currency: "XOF",
    fxRate: 1,
    country: "ci",
  };
  const { currency, fxRate } = currencyContext;

  const masterClassRes = await getMasterclassesBySlug(masterClassSlug);
  if (masterClassRes.ok) {
    masterClassRes.data.originalPrice = masterClassRes.data.price; // Store original XOF price
    masterClassRes.data.price = masterClassRes.data.price * fxRate; // Display converted price
  }

  const titleText =
    locale === "fr"
      ? `Rejoignez ma masterclass - ${masterClassRes.data?.name}`
      : `Join masterclass - ${masterClassRes.data?.name}`;
  const title = `${titleText} • ${PAGE_TITLE}`;
  // console.log(JSON.stringify(masterClassRes));
  return {
    metaTitle: title,
    metaDescription: masterClassRes.data?.description,
    metaOrigin: origin,
    metaLocale: locale,
    masterClassSlug,
    data: masterClassRes.data,
  };
};

// Enrollment Success Info cookie - has to be the same as booking success info cookie
const cookie = createCookie(`_${LARUCHE_IDENTIFIER}_bsi`, {
  maxAge: 60 * 60 * 24, // 1 day
  path: "/", // Path where the cookie is accessible - Entire domain
  secure: process.env.NODE_ENV === "production",
  httpOnly: true,
  sameSite: "lax",
  secrets: [process.env.COOKIE_SECRET as string],
});

export const action = async ({ request, params }: ActionFunctionArgs) => {
  const formData = await request.formData();

  const whatsappPhoneNumber = cleanPhoneNumber(
    formData.get("whatsappPhoneNumber")?.toString()
  );

  const phoneNumber =
    whatsappPhoneNumber ||
    cleanPhoneNumber(formData.get("phoneNumber")?.toString());

  const form_data_string = formData.get("formData")?.toString() || "{}";

  const enrollmentData = {
    class_id: formData.get("masterclassId")?.toString() ?? "",
    first_name: formData.get("firstName")?.toString() ?? "",
    surname: formData.get("surname")?.toString() ?? "",
    dob: dayjs(formData.get("dob") as string).toISOString(),
    gender: formData.get("gender")?.toString() ?? "",
    email: formData.get("email")?.toString() ?? "",
    phone_number: phoneNumber,
    whatsapp_phone_number: whatsappPhoneNumber,
    form_data: JSON.parse(form_data_string),
    promo_code: formData.get("promoCode")?.toString() ?? "",
  };

  const cookiePayload = {
    price: formData.get("cost")?.toString(),
    email: formData.get("email")?.toString(),
    phoneNumber: phoneNumber,
    masterclassId: enrollmentData.class_id,
    masterClassImage: formData.get("masterClassImage")?.toString(),
    discountPercent: formData.get("discountPercent")?.toString(),
    promoCode: +formData.get("discountPercent")!
      ? formData.get("promoCode")?.toString()
      : "", // * If there is no discount don't use promo code
  } as any;

  try {
    console.log("Data to submit:", enrollmentData);
    const res = await api.post(`masterclass/enrollments/`, enrollmentData);
    console.log(res.data);
    cookiePayload.googleCalendarLink = res.data.google_calendar_link;
    cookiePayload.enrollmentId = res.data._id;
    const cookieHeader = await cookie.serialize(cookiePayload);

    return redirect(
      `${params?.lang ? `/${params.lang}` : ""}/payment?enrollmentId=${
        res.data._id
      }`,
      {
        headers: {
          "Set-Cookie": cookieHeader,
        },
      }
    );
  } catch (error: any) {
    console.error("Error enrolling in masterclass:", error);

    // You can check if the error is an AxiosError
    if (isAxiosError(error)) {
      if (error.response) {
        // Handle the response error
        return json(
          { error: error.response.data?.detail ?? "An error occurred." },
          { status: error.response.status }
        );
      }
      // Handle other types of Axios errors (like network errors)
      return json({ error: "Network error." }, { status: 500 });
    }

    // Handle generic errors
    return json({ error: "Failed to enroll in masterclass." }, { status: 500 });
  }
};
